import React, { createContext, useContext, useRef, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuthStore, useStore } from '@core/store';
import { authService } from '@core/auth/AuthService';
import { User } from '../types';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    loginVersion: number;
    login: () => void;
    logout: () => void;
    getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loginVersion, setLoginVersion] = useState(0);
    const { user, setUser, clearAuth, clearCache } = useAuthStore();
    const clearPersisted = useStore((s) => s.clearPersisted);
    const hasRun = useRef(false);

    const clearAuthAndPersisted = useCallback(() => {
        clearPersisted();
        clearAuth();
        clearCache();
    }, [clearPersisted, clearAuth, clearCache]);

    const setUserFromAuth = useCallback(
        (nextUser: User | null) => {
            setUser(nextUser);
            if (nextUser) {
                setLoginVersion((prev) => prev + 1);
            }
        },
        [setUser]
    );

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        authService.setCallbacks(setUserFromAuth, clearAuthAndPersisted);

        authService
            .handlePageLoad()
            .catch((error) => console.error('[AuthProvider] handlePageLoad failed:', error))
            .finally(() => setIsLoading(false));
    }, [setUserFromAuth, clearAuthAndPersisted]);

    const login = () => {
        setIsLoading(true);
        authService.loginRedirect().catch((error) => {
            console.error('[AuthProvider] Login failed:', error);
            setIsLoading(false);
        });
    };

    const logout = () => {
        clearPersisted();
        clearCache();
        authService.logout().catch((error) => {
            console.error('[AuthProvider] Logout failed:', error);
        });
    };

    const getAccessToken = () => authService.getAccessTokenSilent();

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                loginVersion,
                login,
                logout,
                getAccessToken
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
